import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize } from "rxjs";
import { Router } from "@angular/router";
import { AuthService } from "../../services/api/auth/auth.service";
import { DataService } from "../../services/api/data/data.service";
import { UserFieldService } from "../../services/user-field/user-field.service";
import { ThemeService } from "../../services/theme/theme.service";
import { DashboardMap } from "../components/dashboard-map/dashboard-map";
import { DashboardChart } from "../components/dashboard-chart/dashboard-chart";
import { DashboardTableComponent } from "../components/dashboard-table/dashboard-table.component";
import { ModalService } from "../../services/modal/modal.service";
import { LoadingService } from "../../services/loading/loading";
import { alertNoClaimedField } from "../utils/data-util";

@Component({
  selector: "app-entity-dashboard",
  standalone: true,
  imports: [DashboardMap, DashboardChart, DashboardTableComponent],
  templateUrl: "./entity-dashboard.component.html",
  styleUrl: "./entity-dashboard.component.scss",
  host: {
    class: "flex flex-col h-full overflow-hidden",
  },
})
export class EntityDashboardComponent implements OnInit {
  claimedFields?: ClaimedField[];
  claimedFieldETs?: any;
  selectedclaimedField?: ClaimedField;
  claimedFieldsForMap?: any[];
  mode = localStorage.getItem("theme");
  private destroyRef = inject(DestroyRef);

  // Constructor
  constructor(
    private authService: AuthService,
    private dataService: DataService,
    private userFieldService: UserFieldService,
    private themeService: ThemeService,
    private router: Router,
    private modalService: ModalService,
    private loadingService: LoadingService,
  ) {}

  /**
   * Function of ngOnInit Lifecycle Hook
   */
  ngOnInit(): void {
    this.fetchClaimedFields();
    // Subscribe to the selectedUserFieldId observable
    this.userFieldService.selectedUserFieldId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((id: number | null) => {
        if (id) {
          if (!this.claimedFields?.length) {
            return;
          }
          // Switch the local variable selectedclaimedField to the new one
          this.selectedclaimedField = this.claimedFields?.find(
            (userfield) => userfield.id === id,
          );
          if (!this.selectedclaimedField) {
            return;
          }
          this.claimedFieldETs = undefined;
        }
      });
    // Subscribe the mode (dark or light) and pass to Map (Mapbox)
    this.themeService.darkMode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isDarkMode) => {
        this.mode = isDarkMode === true ? "dark" : "light";
      });
  }

  /**
   * Function for fetching claimed fields for a certain manager
   * @param userId
   */
  fetchClaimedFields(): void {
    this.loadingService.start({
      token: "entity-dashboard-claimed-fields",
      message: "Loading fields",
    });
    // Subscribe the getClaimedFieldsManager, process & prepare the data
    this.dataService
      .getClaimedFieldsEntity()
      .pipe(
        finalize(() =>
          this.loadingService.stop("entity-dashboard-claimed-fields"),
        ),
      )
      .subscribe({
        // Process the result (claimedFields)
        next: async (claimedFields: any[]) => {
          // Assign the claimedFields to the local variable
          this.claimedFields = claimedFields;
          // If claimedFields is falsy value, pop up the content: no claimed user in the current manager's district.
          if (!this.claimedFields || this.claimedFields.length === 0) {
            await alertNoClaimedField(this.modalService);
            const mode =
              this.authService.internalModeValue === "demo" ? "demo" : null;
            this.router.navigate(["/entity/map-view"], {
              queryParams: { mode },
              queryParamsHandling: "merge",
            });
            return;
          }
          // Extract properties id, field_id, name and geom from claimedFields for Map Using (Mapbox)
          this.claimedFieldsForMap = this.claimedFields.map(
            ({ id, field_id, username, first_name, last_name, geom }) => {
              const displayName =
                username || [first_name, last_name].filter(Boolean).join(" ");
              return { id, field_id, username: displayName, geom };
            },
          );
        },
        // Error
        error: (error: any) => {
          console.error("Cannot Get Fields for the current Manager", error);
        },
      });
  }
}
